import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { MaskLine, Reveal } from "@/components/site/motion";
import { LEGAL_SECTIONS } from "@/lib/legal";
import { LEGAL } from "@/lib/site";
import { scrollToId, scrollTop } from "@/lib/lenis";

const Section = ({ section }) => (
  <section id={section.id} data-testid={`legal-section-${section.id}`} className="scroll-mt-24 border-t border-line py-14 first:border-t-0 first:pt-0">
    <h2 className="font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">{section.title}</h2>
    <div className="mt-8 space-y-8">
      {section.blocks.map((b) => (
        <Reveal key={b.h} y={20}>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-acid">{b.h}</h3>
          {b.p.map((text) => (
            <p key={text.slice(0, 40)} className="mt-3 max-w-2xl text-base leading-relaxed text-bone/75">{text}</p>
          ))}
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Legal() {
  const { hash } = useLocation();

  useEffect(() => {
    const id = hash.replace("#", "");
    if (!id) {
      scrollTop();
      return undefined;
    }
    const t = setTimeout(() => scrollToId(id), 150);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="min-h-screen" data-testid="legal-page">
      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-line bg-ink/85 px-5 backdrop-blur-xl sm:px-8 lg:px-12">
        <Link to="/" className="flex items-center gap-2.5" data-testid="legal-logo-link">
          <LogoMark className="h-8 w-8" />
          <span className="font-display text-sm font-black tracking-[0.2em]">SFUMO</span>
        </Link>
        <Link to="/" data-testid="legal-back-home" className="flex h-10 items-center gap-2 border border-line px-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors hover:border-acid">
          <ArrowLeft className="h-3.5 w-3.5" /> Torna al sito
        </Link>
      </header>

      <main className="px-5 py-16 sm:px-8 lg:px-12 lg:py-24">
        <h1 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskLine onLoad>Condizioni,</MaskLine>
          <MaskLine onLoad delay={0.1}>spedizioni</MaskLine>
          <MaskLine onLoad delay={0.2} className="text-outline">e privacy.</MaskLine>
        </h1>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">Ultimo aggiornamento · {LEGAL.updated}</p>

        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <nav className="lg:col-span-3">
            <ul className="space-y-2 lg:sticky lg:top-28">
              {LEGAL_SECTIONS.map((s, i) => (
                <li key={s.id}>
                  <button onClick={() => scrollToId(s.id)} data-testid={`legal-toc-${s.id}`} className="group flex items-center gap-3 py-1 text-left font-mono text-[11px] uppercase tracking-[0.2em] text-bone/70 transition-colors hover:text-acid">
                    <span className="text-acid">0{i + 1}</span> {s.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          <div className="lg:col-span-8 lg:col-start-5">
            {LEGAL_SECTIONS.map((s) => (
              <Section key={s.id} section={s} />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
